import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import { makeStyles } from 'tss-react/mui';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Fade,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';
import { getData, postData, getDataFull } from '../../services/ajax';
import { AuthContext } from '../../context/auth';
import { LoadingModalContext } from '../../components/modal/loading';
import { AlertModalContext } from '../../components/modal/alert';
import { useSnackbar } from 'notistack';
import { dateTimeApiToUi, dateTimeUiToApi } from '../../const/dates';
import OrdenTimeline, { ESTADO_LABELS } from '../../components/orden/OrdenTimeline';
import CountdownChip from '../../components/orden/CountdownChip';
import ProcessGuide from '../../components/orden/ProcessGuide';
import OrdenHistorial from '../../components/orden/OrdenHistorial';
import ReprogramarPanel from '../../components/orden/ReprogramarPanel';

const useStyles = makeStyles()((theme) => ({
    paper: {
        padding: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
}));

const ACCENT_MAIN = {
    success: 'success.main',
    error: 'error.main',
    warning: 'warning.main',
    info: 'info.main',
    primary: 'primary.main',
};

function ContactButtons({ celular, email }) {
    const { enqueueSnackbar } = useSnackbar();
    const copy = async (text, label) => {
        try {
            await navigator.clipboard.writeText(text);
            enqueueSnackbar(`${label} copiado`, { variant: 'success' });
        } catch (_) {
            enqueueSnackbar('No se pudo copiar', { variant: 'error' });
        }
    };

    return (
        <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {celular ? (
                <>
                    <Button size="small" variant="outlined" startIcon={<PhoneIcon />} href={`tel:${celular}`}>
                        Llamar
                    </Button>
                    <Tooltip title="Copiar celular">
                        <IconButton size="small" onClick={() => copy(celular, 'Celular')}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </>
            ) : null}
            {email ? (
                <>
                    <Button size="small" variant="outlined" startIcon={<EmailIcon />} href={`mailto:${email}`}>
                        Correo
                    </Button>
                    <Tooltip title="Copiar correo">
                        <IconButton size="small" onClick={() => copy(email, 'Correo')}><ContentCopyIcon fontSize="small" /></IconButton>
                    </Tooltip>
                </>
            ) : null}
            {!celular && !email ? (
                <Typography variant="body2" color="text.secondary">Sin datos de contacto</Typography>
            ) : null}
        </Stack>
    );
}

function ActionPanel({ title, children, accent = 'primary', chip = null }) {
    const borderColor = ACCENT_MAIN[accent] || ACCENT_MAIN.primary;
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 2,
                borderLeft: '4px solid',
                borderLeftColor: borderColor,
                bgcolor: (theme) => {
                    const key = accent === 'primary' ? 'primary' : accent;
                    const main = theme.palette[key]?.main;
                    if (!main) return 'transparent';
                    return `${main}0F`;
                },
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap', mb: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
                {chip}
            </Stack>
            {children}
        </Paper>
    );
}

function Page() {
    const { classes } = useStyles();
    const { id } = useParams();
    const { usuario } = useContext(AuthContext);
    const navigate = useNavigate();
    const loadingModal = useContext(LoadingModalContext);
    const alertModal = useContext(AlertModalContext);
    const { enqueueSnackbar } = useSnackbar();

    const [orden, setOrden] = useState(null);
    const [establecimientos, setEstablecimientos] = useState([]);

    const isAdmin = !!(usuario?.es_admin || usuario?.nivel_usuario === 1);
    const puedeResponder = !!(usuario?.puede_responder || isAdmin);
    const estId = usuario?.establecimiento_id;

    const load = useCallback(async () => {
        loadingModal.setOpen(true);
        try {
            const response = await getData(`ordenes-practicas/${id}`, usuario.token);
            if (response.error === '') setOrden(response.data);
            else enqueueSnackbar(response.error, { variant: 'error' });
        } finally {
            loadingModal.setOpen(false);
        }
    }, [id, usuario.token]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!isAdmin) return;
        getDataFull('establecimientos?per_page=500', usuario.token).then((r) => {
            if (r.error === '' && r.data) setEstablecimientos(r.data.filter((e) => e.activo !== false));
        });
    }, [isAdmin, usuario.token]);

    const runAction = async (path, body, okMsg) => {
        loadingModal.setOpen(true);
        try {
            const response = await postData(`ordenes-practicas/${id}/${path}`, body, usuario.token);
            if (response.error === '') {
                enqueueSnackbar(okMsg, { variant: 'success' });
                setOrden(response.data);
            } else {
                enqueueSnackbar(response.error, { variant: 'error' });
            }
        } finally {
            loadingModal.setOpen(false);
        }
    };

    const mismoEstablecimiento = orden && Number(orden.establecimiento_id) === Number(estId);
    const showResponder = orden?.estado === 'asignada' && (isAdmin || (puedeResponder && mismoEstablecimiento));
    const showCerrar = orden?.estado === 'con_turno' && (isAdmin || mismoEstablecimiento);
    const showReprogramar = orden?.estado === 'con_turno' && (isAdmin || mismoEstablecimiento);
    const showCancelarTurno = orden?.estado === 'con_turno' && (isAdmin || mismoEstablecimiento);
    const showInsumo = orden && ['pendiente', 'vencido'].includes(orden.insumo_estado)
        && (isAdmin || Number(orden.establecimiento_origen_id) === Number(estId));
    const showDestrabar = isAdmin && orden?.estado === 'pendiente_manual';
    const showCancelar = isAdmin && orden && !['cerrada', 'cancelada'].includes(orden.estado);

    const guideSeverity = useMemo(() => {
        if (!orden) return 'info';
        if (orden.estado === 'con_turno' && orden.turno_ya_ocurrio) return 'success';
        if (orden.estado === 'con_turno') {
            const max = Number(orden.reprogramaciones_max ?? 2);
            const count = Number(orden.reprogramaciones_count ?? 0);
            if (max > 0 && max - count === 1) return 'warning';
        }
        return 'info';
    }, [orden]);

    const guideSteps = useMemo(() => {
        if (!orden) return [];
        switch (orden.estado) {
            case 'pendiente_insumo':
                return ['Registrar la compra del insumo o, si corresponde, forzar el avance.', 'El sistema iniciará la asignación automática.'];
            case 'asignada':
                return [
                    'Aceptar con fecha de turno o rechazar con motivo.',
                    'El rechazo libera la orden para otro establecimiento.',
                ];
            case 'con_turno':
                return [
                    'Avisar al paciente con los datos de contacto.',
                    'Reprogramar el turno solo dentro de los límites de la orden.',
                    'Cerrar la atención al efectivizar (también antes de la hora, solo como efectivizada).',
                    'Cancelar el turno libera la orden para reasignación.',
                ];
            case 'pendiente_manual':
                return ['Reasignar a un establecimiento o indicar turno directo.'];
            default:
                return ['Consultar el historial y los plazos vigentes de la orden.'];
        }
    }, [orden]);

    if (!orden) {
        return (
            <Layout nombrePagina="Detalle de orden">
                <Grid size={12}><Typography>Cargando…</Typography></Grid>
            </Layout>
        );
    }

    return (
        <Layout nombrePagina={`Orden ${orden.codigo || orden.id}`}>
            <Fade in timeout={900}>
                <Grid size={12}>
                    <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
                        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/orden-practica')}>Volver</Button>
                        <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>
                            {orden.codigo} — {ESTADO_LABELS[orden.estado] || orden.estado}
                        </Typography>
                    </Stack>

                    <ProcessGuide title="Qué hacer ahora" steps={guideSteps} severity={guideSeverity} />

                    <Paper className={classes.paper}>
                        <OrdenTimeline estado={orden.estado} requiereInsumo={!!orden.requiere_insumo} />
                        <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 2, flexWrap: 'wrap' }}>
                            {orden.tiempos_restantes?.respuesta != null && (
                                <CountdownChip
                                    label="Respuesta"
                                    remainingSeconds={orden.tiempos_restantes.respuesta}
                                    endsAt={orden.respuesta_vence_at}
                                    totalSeconds={(orden.plazo_respuesta_minutos || 0) * 60}
                                />
                            )}
                            {orden.tiempos_restantes?.sin_turno != null && (
                                <CountdownChip
                                    label="Sin turno"
                                    remainingSeconds={orden.tiempos_restantes.sin_turno}
                                    endsAt={orden.sin_turno_vence_at}
                                    totalSeconds={(orden.plazo_max_sin_turno_minutos || 0) * 60}
                                />
                            )}
                            {orden.tiempos_restantes?.insumo != null && (
                                <CountdownChip
                                    label="Insumo"
                                    remainingSeconds={orden.tiempos_restantes.insumo}
                                    endsAt={orden.insumo_vence_at}
                                    totalSeconds={(orden.plazo_compra_horas || 0) * 3600}
                                />
                            )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">Paciente</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{orden.paciente_nombre}</Typography>
                                <Box sx={{ mt: 1 }}>
                                    <ContactButtons celular={orden.paciente_celular} email={orden.paciente_email} />
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Typography variant="subtitle2" color="text.secondary">Práctica</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 600 }}>{orden.practica_nombre}</Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Destino: {orden.establecimiento_nombre || 'Sin asignar'}
                                </Typography>
                                <Typography variant="body2">
                                    Origen: {orden.establecimiento_origen_nombre || '—'}
                                </Typography>
                                {orden.turno_en ? (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Turno: {dateTimeApiToUi(orden.turno_en)}
                                    </Typography>
                                ) : null}
                                {orden.creador_nombre ? (
                                    <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                                        Creada por {orden.creador_nombre}
                                    </Typography>
                                ) : null}
                            </Grid>
                        </Grid>
                    </Paper>

                    {showResponder && (
                        <ActionPanel title="Responder asignación" accent="success">
                            <ResponderForm
                                onAceptar={(v) => runAction('aceptar-turno', v, 'Turno aceptado')}
                                onRechazar={(motivo) => runAction('rechazar', { motivo }, 'Orden rechazada')}
                            />
                        </ActionPanel>
                    )}

                    {showCerrar && (
                        <ActionPanel
                            title="Cerrar atención"
                            accent={orden.turno_ya_ocurrio ? 'success' : 'info'}
                            chip={(
                                <Chip
                                    size="small"
                                    color={orden.turno_ya_ocurrio ? 'default' : 'info'}
                                    label={orden.turno_ya_ocurrio ? 'Turno en curso o vencido' : 'Antes de la hora programada'}
                                />
                            )}
                        >
                            <CerrarForm
                                turnoYaOcurrio={!!orden.turno_ya_ocurrio}
                                onSubmit={(v) => runAction('cerrar', v, 'Atención registrada')}
                            />
                        </ActionPanel>
                    )}

                    {showReprogramar && (
                        <ReprogramarPanel
                            orden={orden}
                            onSubmit={(v) => runAction('reprogramar', v, 'Turno reprogramado.')}
                        />
                    )}

                    {showCancelarTurno && (
                        <ActionPanel title="Cancelar turno" accent="warning">
                            <CancelarTurnoForm
                                onSubmit={(motivo) => alertModal.showModal(
                                    'Cancelar turno',
                                    'La orden quedará disponible para reasignación. ¿Continuar?',
                                    () => runAction('cancelar-turno', { motivo }, 'Turno cancelado. La orden vuelve a asignación.')
                                )}
                            />
                        </ActionPanel>
                    )}

                    {showInsumo && (
                        <ActionPanel title="Registrar insumo" accent="info">
                            <InsumoForm
                                onSubmit={(v) => runAction('registrar-insumo', v, 'Insumo registrado')}
                                onForzar={isAdmin && orden.estado === 'pendiente_insumo'
                                    ? () => alertModal.showModal(
                                        'Forzar asignación',
                                        'Se iniciará la asignación sin registrar la compra. ¿Continuar?',
                                        () => runAction('forzar-desde-insumo', {}, 'Asignación forzada')
                                    )
                                    : null}
                            />
                        </ActionPanel>
                    )}

                    {showDestrabar && (
                        <ActionPanel title="Destrabar orden" accent="warning">
                            <DestrabarForm
                                establecimientos={establecimientos}
                                onSubmit={(v) => runAction('destrabar', v, 'Orden destrabada')}
                            />
                        </ActionPanel>
                    )}

                    {showCancelar && (
                        <ActionPanel title="Cancelar orden" accent="error">
                            <CancelarForm
                                onSubmit={(motivo) => alertModal.showModal(
                                    'Cancelar orden',
                                    'La orden quedará cancelada. ¿Continuar?',
                                    () => runAction('cancelar', { motivo }, 'Orden cancelada')
                                )}
                            />
                        </ActionPanel>
                    )}

                    <OrdenHistorial eventos={orden.eventos || []} />
                </Grid>
            </Fade>
        </Layout>
    );
}

function ResponderForm({ onAceptar, onRechazar }) {
    const [turnoEn, setTurnoEn] = useState('');
    const [obs, setObs] = useState('');
    const [motivo, setMotivo] = useState('');

    return (
        <Stack spacing={2}>
            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    borderLeft: '3px solid',
                    borderLeftColor: 'success.main',
                    bgcolor: (t) => `${t.palette.success.main}0A`,
                }}
            >
                <Stack spacing={2}>
                    <TextField
                        label="Fecha y hora del turno"
                        type="datetime-local"
                        value={turnoEn}
                        onChange={(e) => setTurnoEn(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Observaciones del turno"
                        value={obs}
                        onChange={(e) => setObs(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                    />
                    <Button
                        variant="contained"
                        color="success"
                        disabled={!turnoEn}
                        onClick={() => onAceptar({
                            turno_en: dateTimeUiToApi(turnoEn),
                            turno_observaciones: obs || null,
                        })}
                    >
                        Aceptar con turno
                    </Button>
                </Stack>
            </Box>

            <Box
                sx={{
                    p: 1.5,
                    borderRadius: 1,
                    borderLeft: '3px solid',
                    borderLeftColor: 'error.main',
                    bgcolor: (t) => `${t.palette.error.main}0A`,
                }}
            >
                <Stack spacing={1.5}>
                    <Alert severity="info" sx={{ py: 0.5 }}>
                        El motivo queda registrado junto con quién rechazó, cuándo y el establecimiento asignado.
                    </Alert>
                    <TextField
                        label="Motivo del rechazo"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        fullWidth
                        size="small"
                        required
                        helperText="Obligatorio. Queda asentado en el historial."
                    />
                    <Button
                        variant="outlined"
                        color="error"
                        disabled={!motivo.trim()}
                        onClick={() => onRechazar(motivo.trim())}
                    >
                        Rechazar
                    </Button>
                </Stack>
            </Box>
        </Stack>
    );
}

const CIERRE_OPCIONES = [
    { value: 'efectivizado', label: 'Efectivizado', color: 'success' },
    { value: 'no_asistio', label: 'No asistió', color: 'warning' },
    { value: 'cancelado', label: 'Cancelado', color: 'error' },
    { value: 'otro', label: 'Otro', color: 'default' },
];

function CerrarForm({ onSubmit, turnoYaOcurrio }) {
    const anticipado = !turnoYaOcurrio;
    const [resultado, setResultado] = useState('efectivizado');
    const [detalles, setDetalles] = useState('');

    useEffect(() => {
        if (anticipado) setResultado('efectivizado');
    }, [anticipado]);

    return (
        <Stack spacing={2}>
            {anticipado ? (
                <Alert severity="info">
                    Se registra la atención como efectivizada antes de la hora programada.
                    No asistió, Cancelado y Otro se habilitan después de la fecha y hora del turno.
                </Alert>
            ) : null}

            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                {CIERRE_OPCIONES.map((op) => {
                    const bloqueada = anticipado && op.value !== 'efectivizado';
                    const chip = (
                        <Chip
                            key={op.value}
                            label={op.label}
                            color={op.color === 'default' ? 'default' : op.color}
                            variant={resultado === op.value ? 'filled' : 'outlined'}
                            onClick={bloqueada ? undefined : () => setResultado(op.value)}
                            disabled={bloqueada}
                            aria-disabled={bloqueada}
                            sx={{ cursor: bloqueada ? 'not-allowed' : 'pointer' }}
                        />
                    );
                    if (bloqueada) {
                        return (
                            <Tooltip key={op.value} title="Disponible después de la fecha y hora del turno.">
                                <span>{chip}</span>
                            </Tooltip>
                        );
                    }
                    return chip;
                })}
            </Stack>

            <TextField
                label="Detalles"
                value={detalles}
                onChange={(e) => setDetalles(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
            />
            <Button
                variant="contained"
                color={resultado === 'efectivizado' ? 'success' : 'primary'}
                onClick={() => onSubmit({
                    cierre_resultado: anticipado ? 'efectivizado' : resultado,
                    cierre_detalles: detalles || null,
                })}
            >
                Registrar cierre
            </Button>
        </Stack>
    );
}

function CancelarTurnoForm({ onSubmit }) {
    const [motivo, setMotivo] = useState('');
    return (
        <Stack spacing={2}>
            <Alert severity="warning">
                La orden queda disponible para reasignación. Queda registrado quién canceló, cuándo y el establecimiento.
            </Alert>
            <TextField
                label="Motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                fullWidth
                required
                size="small"
                helperText="Obligatorio. Queda asentado en el historial."
            />
            <Button
                variant="outlined"
                color="warning"
                disabled={!motivo.trim()}
                onClick={() => onSubmit(motivo.trim())}
            >
                Cancelar turno
            </Button>
        </Stack>
    );
}

function InsumoForm({ onSubmit, onForzar }) {
    const [origen, setOrigen] = useState('');
    const [obs, setObs] = useState('');
    return (
        <Stack spacing={2}>
            <TextField
                label="Origen / proveedor del insumo"
                value={origen}
                onChange={(e) => setOrigen(e.target.value)}
                fullWidth
                size="small"
                required
            />
            <TextField
                label="Observaciones"
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                size="small"
            />
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                <Button
                    variant="contained"
                    disabled={!origen.trim()}
                    onClick={() => onSubmit({ insumo_origen: origen, insumo_observaciones: obs || null })}
                >
                    Registrar compra
                </Button>
                {onForzar ? (
                    <Button variant="outlined" color="warning" onClick={onForzar}>Forzar asignación</Button>
                ) : null}
            </Stack>
        </Stack>
    );
}

function DestrabarForm({ establecimientos, onSubmit }) {
    const [modo, setModo] = useState('reasignar');
    const [estId, setEstId] = useState('');
    const [turnoEn, setTurnoEn] = useState('');
    return (
        <Stack spacing={2}>
            <TextField select label="Modo" value={modo} onChange={(e) => setModo(e.target.value)} fullWidth size="small">
                <MenuItem value="reasignar">Reasignar a establecimiento</MenuItem>
                <MenuItem value="turno_directo">Turno directo</MenuItem>
            </TextField>
            <TextField
                select
                label="Establecimiento"
                value={estId}
                onChange={(e) => setEstId(e.target.value)}
                fullWidth
                size="small"
            >
                {establecimientos.map((e) => (
                    <MenuItem key={e.id} value={e.id}>{e.nombre} ({e.tipo})</MenuItem>
                ))}
            </TextField>
            {modo === 'turno_directo' ? (
                <TextField
                    label="Fecha y hora del turno"
                    type="datetime-local"
                    value={turnoEn}
                    onChange={(e) => setTurnoEn(e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                    size="small"
                />
            ) : null}
            <Button
                variant="contained"
                color="warning"
                disabled={!estId || (modo === 'turno_directo' && !turnoEn)}
                onClick={() => onSubmit({
                    modo,
                    establecimiento_id: Number(estId),
                    turno_en: modo === 'turno_directo' ? dateTimeUiToApi(turnoEn) : undefined,
                })}
            >
                Destrabar
            </Button>
        </Stack>
    );
}

function CancelarForm({ onSubmit }) {
    const [motivo, setMotivo] = useState('');
    return (
        <Stack spacing={2}>
            <Alert severity="error">La cancelación es definitiva y solo la realiza un administrador.</Alert>
            <TextField
                label="Motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                fullWidth
                required
                size="small"
            />
            <Button
                variant="outlined"
                color="error"
                disabled={!motivo.trim()}
                onClick={() => onSubmit(motivo)}
            >
                Cancelar orden
            </Button>
        </Stack>
    );
}

export default Page;
